import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import postSitePreviewChartData from "../http/postSitePreviewChartData";
import { ReloginContext } from "../ReloginContext";
import {
  HalfHourlyChartDataColumnar,
  HalfHourlyChartDataPoint,
  HalfHourlyChartDataResponse,
} from "./useChartData";
import { getAuthTokenBlocking } from "./utils";

// Helper function to convert columnar format to row format for half-hourly data
const convertChartDataColumnarToRows = (
  columnar: HalfHourlyChartDataColumnar
): HalfHourlyChartDataPoint[] => {
  const length = columnar.x.length;
  const rows: HalfHourlyChartDataPoint[] = [];

  for (let i = 0; i < length; i++) {
    rows.push({
      x: columnar.x[i],
      fGeneration: columnar.fGeneration[i],
      fDemand: columnar.fDemand[i],
      fBehindMeterPV: columnar.fBehindMeterPV[i],
      fBatteryFromPV: columnar.fBatteryFromPV[i],
      fBatteryFromGrid: columnar.fBatteryFromGrid[i],
      fBatteryFinalCharge: columnar.fBatteryFinalCharge[i],
      fBehindMeterBattery: columnar.fBehindMeterBattery[i],
      fBehindMeterBatteryGrid: columnar.fBehindMeterBatteryGrid[i],
      fCurtailed: columnar.fCurtailed?.[i],
      fExportNetwork: columnar.fExportNetwork[i],
      fExportGrid: columnar.fExportGrid[i],
      fImportGrid: columnar.fImportGrid[i],
    });
  }

  return rows;
};

const usePreviewChartData = ({
  dataType,
  options,
  payload,
  enabled = true,
}: {
  dataType: "energy_flows";
  options: object;
  payload: any;
  enabled?: boolean;
}): UseQueryResult<HalfHourlyChartDataResponse, unknown> => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useQuery(
    ["chart-data", dataType, payload, options],
    async () => {
      const response = await postSitePreviewChartData(
        await getAuthTokenBlocking(getToken, relogin),
        dataType,
        options,
        payload
      );

      // Transform columnar format to row format
      return {
        siteId: response.siteId,
        data: convertChartDataColumnarToRows(
          response.data as HalfHourlyChartDataColumnar
        ),
      } as HalfHourlyChartDataResponse;
    },
    {
      enabled: enabled && !!payload,
      // Cache for 5 minutes since this data is relatively stable
      staleTime: 5 * 60 * 1000,
    }
  );
};

export default usePreviewChartData;
